/**
 * KnowledgeTab Component
 *
 * Knowledge base configuration and category-scoped document management.
 * Features:
 * - Enable/disable knowledge base
 * - Per-category retrieval rules (query mode, fixed query, limits)
 * - Category-organized document browser with search
 * - Document upload/create with required category
 * - Category management (filter, rename, delete)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import {
  BookOpen, Search, Plus, Trash2, RefreshCw, Settings, FileText, X, Upload,
  Link, RotateCcw, Tag, ChevronDown, ChevronRight, Zap, MessageSquare,
  GripVertical, Layers, Globe, Wrench, Inbox, Copy, Check, AlertCircle,
} from 'lucide-react';
import { getConfigValue } from '@/config/fieldConfig';
import { knowledgeApi, type KnowledgeDocument, type DocumentListResponse } from '@/lib/api/knowledge';
import { KnowledgeDocList } from './KnowledgeDocList';
import { useI18n } from '@/i18n';
import type { AgentConfiguration } from '@/types';

const DEFAULT_USAGE_GUIDANCE = `以下是与用户问题相关的知识库内容，请结合这些信息回答问题。
- 优先使用知识库中的准确信息
- 如果知识库内容与你的理解冲突，以知识库为准
- 适当引用来源可以增加回答的可信度`;

// Category color palette for visual distinction
const CATEGORY_COLORS: Record<string, { bg: string; text: string; border: string; dot: string }> = {};
const COLOR_PALETTE = [
  { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500' },
  { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
  { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', dot: 'bg-purple-500' },
  { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', dot: 'bg-rose-500' },
  { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200', dot: 'bg-cyan-500' },
  { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', dot: 'bg-orange-500' },
  { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', dot: 'bg-indigo-500' },
];

export function getCategoryColor(name: string) {
  if (!CATEGORY_COLORS[name]) {
    const idx = Object.keys(CATEGORY_COLORS).length % COLOR_PALETTE.length;
    CATEGORY_COLORS[name] = COLOR_PALETTE[idx];
  }
  return CATEGORY_COLORS[name];
}

interface CategoryRule {
  category: string;
  query_mode: 'user_message' | 'fixed';
  fixed_query: string;
  limit?: number | null;
  threshold?: number | null;
  max_content_length?: number | null;
  usage_guidance: string;
  curate_prompt?: string;
}

const CURATE_TEMPLATES = [
  { label: '提取要点', prompt: '请从以下内容中提取核心要点，以简洁的条目形式列出，去除冗余信息。' },
  { label: '生成 FAQ', prompt: '请将以下内容整理为问答对(FAQ)格式，每个问答包含一个问题和简明回答。' },
  { label: '精简为示例', prompt: '请将以下内容精简为简洁的示例文本，保留核心表达和风格特点，适合作为写作参考。' },
];

interface KnowledgeTabProps {
  config: Partial<AgentConfiguration>;
  onChange: (name: string, value: any) => void;
  appContext: any;
}

export const KnowledgeTab: React.FC<KnowledgeTabProps> = ({
  config,
  onChange,
  appContext: _appContext,
}) => {
  const { messages } = useI18n();
  const t = messages.agentConfig?.knowledge;
  const isEnabled = getConfigValue(config, 'knowledge_enabled') === true;
  const agentType = config.agent_type || 'dora';

  const knowledgeHookConfig: any = config.hooks?.knowledge_context || {
    enabled: false,
    agentic_tool_enabled: false,
    limit: 5,
    threshold: 0.3,
    max_content_length: 500,
    include_metadata: false,
    usage_guidance: '',
    categories: [],
  };

  const perRequestEnabled = knowledgeHookConfig.enabled === true;
  const agenticToolEnabled = knowledgeHookConfig.agentic_tool_enabled === true;
  const noModeSelected = isEnabled && !perRequestEnabled && !agenticToolEnabled;

  // State
  const [documents, setDocuments] = useState<KnowledgeDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<KnowledgeDocument[] | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addMode, setAddMode] = useState<'text' | 'file' | 'url' | 'web'>('text');
  const [newDocContent, setNewDocContent] = useState('');
  const [metadataFields, setMetadataFields] = useState<{key: string; value: string}[]>([]);
  const [newDocName, setNewDocName] = useState('');
  const [newDocDescription, setNewDocDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [urlToImport, setUrlToImport] = useState('');
  const [webSearchQuery, setWebSearchQuery] = useState('');
  const [webSearchResults, setWebSearchResults] = useState<any[]>([]);
  const [webSearchLoading, setWebSearchLoading] = useState(false);
  const [selectedResults, setSelectedResults] = useState<Set<number>>(new Set());
  const [showCuratePanel, setShowCuratePanel] = useState(false);
  const [curatePrompt, setCuratePrompt] = useState('');
  const [curateLoading, setCurateLoading] = useState(false);
  const [curatedPreview, setCuratedPreview] = useState<any[]>([]);
  const [promptHistory, setPromptHistory] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(`kb_prompt_history_${agentType}`) || '[]');
    } catch { return []; }
  });
  const [newDocCategory, setNewDocCategory] = useState('default');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [categories, setCategories] = useState<{name: string; doc_count: number}[]>([]);
  const [showRulesSection, setShowRulesSection] = useState(false);
  const [editingRule, setEditingRule] = useState<number | null>(null);
  const [suggestingCategory, setSuggestingCategory] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [recategorizing, setRecategorizing] = useState(false);
  const [toolNameCopied, setToolNameCopied] = useState(false);

  const pageSize = 20;

  // Category rules from config
  const categoryRules: CategoryRule[] = knowledgeHookConfig.categories || [];

  const loadCategories = useCallback(async () => {
    if (!isEnabled) return;
    try {
      const response = await knowledgeApi.listCategories(agentType);
      setCategories(response.categories);
    } catch {
      // Non-critical
    }
  }, [agentType, isEnabled]);

  const loadDocuments = useCallback(async () => {
    if (!isEnabled) return;
    setLoading(true);
    setError(null);
    try {
      const response: DocumentListResponse = await knowledgeApi.list(agentType, {
        limit: pageSize,
        offset: page * pageSize,
        category: activeCategory || undefined,
      });
      setDocuments(response.documents);
      setTotal(response.total);
    } catch {
      // Silent — empty state is shown when no documents (table may not exist yet)
      setDocuments([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [agentType, isEnabled, page, activeCategory]);

  useEffect(() => {
    if (isEnabled) {
      loadDocuments();
      loadCategories();
    }
  }, [isEnabled, loadDocuments, loadCategories]);

  const handleToggle = () => {
    const newValue = !isEnabled;
    onChange('knowledge_enabled', newValue);
    if (!newValue) {
      const newHooksConfig = {
        ...(config.hooks || {}),
        knowledge_context: {
          ...knowledgeHookConfig,
          enabled: false,
          agentic_tool_enabled: false,
        },
      };
      onChange('hooks', newHooksConfig);
    }
  };

  const copyToolName = async () => {
    try {
      await navigator.clipboard.writeText('search_knowledge_base');
      setToolNameCopied(true);
      setTimeout(() => setToolNameCopied(false), 1500);
    } catch {
      // clipboard write may fail in some browsers; fall back silently
    }
  };

  const updateHookConfig = (field: string, value: any) => {
    const newHooksConfig = {
      ...(config.hooks || {}),
      knowledge_context: {
        ...knowledgeHookConfig,
        [field]: value,
      },
    };
    onChange('hooks', newHooksConfig);
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await knowledgeApi.search(agentType, searchQuery, 10, activeCategory || undefined);
      setSearchResults(response.documents);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  const resetAddForm = () => {
    setShowAddForm(false);
    setNewDocContent('');
    setMetadataFields([]);
    setNewDocName('');
    setNewDocDescription('');
    setNewDocCategory(activeCategory || 'default');
    setSelectedFile(null);
    setUrlToImport('');
    setWebSearchQuery('');
    setWebSearchResults([]);
    setSelectedResults(new Set());
    setShowCuratePanel(false);
    setCuratePrompt('');
    setCuratedPreview([]);
  };

  const handleSuggestCategory = async (content: string) => {
    if (!content.trim() || suggestingCategory) return;
    setSuggestingCategory(true);
    try {
      const result = await knowledgeApi.suggestCategory(agentType, content.slice(0, 2000));
      setNewDocCategory(result.suggested_category);
    } catch {
      // Silent — manual category selection still works
    } finally {
      setSuggestingCategory(false);
    }
  };

  const handleAddDocument = async () => {
    setLoading(true);
    setError(null);
    try {
      let metadata: Record<string, any> | undefined;
      const validFields = metadataFields.filter(f => f.key.trim());
      if (validFields.length > 0) {
        metadata = {};
        for (const f of validFields) {
          metadata[f.key.trim()] = f.value;
        }
      }

      if (addMode === 'text') {
        if (!newDocContent.trim()) { setError('请输入文档内容'); setLoading(false); return; }
        if (bulkMode) {
          const lines = newDocContent.split('\n').map(l => l.trim()).filter(l => l);
          if (lines.length === 0) { setError('没有有效内容'); setLoading(false); return; }
          const useCategory = newDocCategory === 'default' ? 'auto' : newDocCategory;
          const result = await knowledgeApi.bulkCreate(agentType, lines, useCategory);
          if (result.categories) {
            const catSummary = Object.entries(result.categories)
              .sort(([, a], [, b]) => b - a)
              .map(([name, count]) => `${name}(${count})`)
              .join('、');
            alert(`已导入 ${result.created} 条，自动分类：${catSummary}`);
          }
          if (result.failed > 0) {
            setError(`${result.created} 条成功，${result.failed} 条失败`);
          }
        } else {
          await knowledgeApi.create(agentType, newDocContent, newDocCategory, metadata);
        }
      } else if (addMode === 'file') {
        if (!selectedFile) { setError('请选择文件'); setLoading(false); return; }
        await knowledgeApi.uploadFile(agentType, selectedFile, newDocCategory, newDocName || selectedFile.name, newDocDescription, metadata);
      } else if (addMode === 'url') {
        if (!urlToImport.trim()) { setError('请输入URL'); setLoading(false); return; }
        await knowledgeApi.importFromUrl(agentType, urlToImport, newDocCategory, newDocName, newDocDescription, metadata);
      }

      resetAddForm();
      loadDocuments();
      loadCategories();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to add document');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async (docId: string) => {
    if (!confirm(t?.confirmDeleteDoc || '确认删除此文档？')) return;
    setLoading(true);
    setError(null);
    try {
      await knowledgeApi.delete(agentType, docId);
      loadDocuments();
      loadCategories();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete document');
    } finally {
      setLoading(false);
    }
  };

  const handleClearAll = async () => {
    if (!confirm(t?.confirmClearAll || '确认清空所有文档？此操作不可撤销！')) return;
    setLoading(true);
    setError(null);
    try {
      await knowledgeApi.clear(agentType);
      setDocuments([]);
      setTotal(0);
      setCategories([]);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to clear documents');
    } finally {
      setLoading(false);
    }
  };

  const handleRecategorize = async () => {
    if (!confirm('确认重新分类所有文档？将使用 AI 按风格自动归类。')) return;
    setRecategorizing(true);
    setError(null);
    try {
      const result = await knowledgeApi.recategorize(agentType);
      const catSummary = Object.entries(result.categories)
        .sort(([, a], [, b]) => b - a)
        .map(([name, count]) => `${name}(${count})`)
        .join('、');
      alert(`重新分类完成！${result.updated} 条已更新。\n分布：${catSummary}`);
      loadDocuments();
      loadCategories();
    } catch (err: any) {
      setError(err.response?.data?.detail || '重新分类失败');
    } finally {
      setRecategorizing(false);
    }
  };

  // Web search handlers
  const handleWebSearch = async () => {
    if (!webSearchQuery.trim()) return;
    setWebSearchLoading(true);
    setWebSearchResults([]);
    setSelectedResults(new Set());
    setShowCuratePanel(false);
    setCuratedPreview([]);
    try {
      const result = await knowledgeApi.searchWeb(webSearchQuery);
      setWebSearchResults(result.results);
      if (result.error) setError(result.error);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.responseData?.detail || 'Web search failed');
    } finally {
      setWebSearchLoading(false);
    }
  };

  const toggleResultSelection = (index: number) => {
    setSelectedResults(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const handleDirectImport = async () => {
    setLoading(true);
    setError(null);
    try {
      for (const idx of selectedResults) {
        const r = webSearchResults[idx];
        const content = r.summary || r.snippet;
        if (content) {
          await knowledgeApi.create(agentType, `${r.title}\n\n${content}\n\n来源: ${r.url}`, newDocCategory, { source_url: r.url, source: 'web_search' });
        }
      }
      resetAddForm();
      loadDocuments();
      loadCategories();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.responseData?.detail || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  const savePromptHistory = (prompt: string) => {
    const updated = [prompt, ...promptHistory.filter(p => p !== prompt)].slice(0, 5);
    setPromptHistory(updated);
    localStorage.setItem(`kb_prompt_history_${agentType}`, JSON.stringify(updated));
  };

  const handleCurate = async () => {
    if (!curatePrompt.trim()) return;
    setCurateLoading(true);
    setError(null);
    try {
      const urls = Array.from(selectedResults).map(i => webSearchResults[i].url);
      const result = await knowledgeApi.curate(agentType, urls, curatePrompt, newDocCategory);
      setCuratedPreview(result.documents);
      if (result.errors.length > 0) {
        setError(`${result.errors.length} 个URL处理失败`);
      }
      savePromptHistory(curatePrompt);
      loadDocuments();
      loadCategories();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.responseData?.detail || 'Curation failed');
    } finally {
      setCurateLoading(false);
    }
  };

  // Auto-populate curate prompt from category rule
  useEffect(() => {
    if (showCuratePanel && newDocCategory) {
      const rule = categoryRules.find((r: any) => r.category === newDocCategory);
      if (rule?.curate_prompt && !curatePrompt) {
        setCuratePrompt(rule.curate_prompt);
      }
    }
  }, [showCuratePanel, newDocCategory]);

  // Category rule management
  const addCategoryRule = () => {
    const newRules = [...categoryRules, {
      category: '',
      query_mode: 'user_message' as const,
      fixed_query: '',
      limit: null,
      threshold: null,
      max_content_length: null,
      usage_guidance: '',
    }];
    updateHookConfig('categories', newRules);
    setEditingRule(newRules.length - 1);
  };

  const updateCategoryRule = (index: number, field: string, value: any) => {
    const newRules = [...categoryRules];
    newRules[index] = { ...newRules[index], [field]: value };
    updateHookConfig('categories', newRules);
  };

  const removeCategoryRule = (index: number) => {
    const newRules = categoryRules.filter((_, i) => i !== index);
    updateHookConfig('categories', newRules);
    setEditingRule(null);
  };

  const displayDocuments = searchResults ?? documents;
  const totalDocs = categories.reduce((sum, c) => sum + c.doc_count, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900">知识库配置</h2>
        <p className="text-sm text-slate-500 mt-1">向量知识库用于上下文感知响应</p>
      </div>

      {/* Enable Toggle */}
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-xl ${isEnabled ? 'bg-green-100' : 'bg-slate-100'}`}>
              <BookOpen className={`w-6 h-6 ${isEnabled ? 'text-green-600' : 'text-slate-400'}`} />
            </div>
            <div>
              <h3 className="font-medium text-slate-900">启用知识库</h3>
              <p className="text-sm text-slate-500">启用后，Agent 将使用向量知识库来增强响应的上下文感知能力</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleToggle}
            className={`relative w-14 h-7 rounded-full transition-colors ${isEnabled ? 'bg-green-500' : 'bg-slate-300'}`}
          >
            <div className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${isEnabled ? 'translate-x-8' : 'translate-x-1'}`} />
          </button>
        </div>
        {isEnabled && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="flex items-center gap-2 text-sm text-green-600">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              知识库已启用
            </div>
          </div>
        )}
      </Card>

      {/* Retrieval Modes */}
      {isEnabled && (
        <Card className={`p-6 ${noModeSelected ? 'border-red-300 bg-red-50/30' : ''}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Settings className="w-5 h-5 text-slate-600" />
              <h3 className="font-medium text-slate-900">检索模式</h3>
              <span className="text-xs text-slate-500">（至少启用一种）</span>
            </div>
          </div>

          {noModeSelected && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">未选择检索模式</p>
                <p className="text-xs mt-0.5">启用知识库后必须至少选择一种检索模式，否则保存时后端会拒绝。</p>
              </div>
            </div>
          )}

          {/* Per-request injection toggle + structure preview */}
          <div className={`rounded-lg border p-4 ${perRequestEnabled ? 'border-blue-200 bg-blue-50/40' : 'border-slate-200 bg-white'}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className={`p-2 rounded-lg ${perRequestEnabled ? 'bg-blue-100' : 'bg-slate-100'}`}>
                  <Inbox className={`w-5 h-5 ${perRequestEnabled ? 'text-blue-600' : 'text-slate-400'}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="font-medium text-slate-900">每次请求自动注入到 System Prompt</h4>
                  <p className="text-sm text-slate-500 mt-0.5">
                    对话时自动检索相关文档，按结构化 XML 块注入到系统提示词。适合 fewshot 语录、参考资料这类"每次都要看到"的场景。
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => updateHookConfig('enabled', !perRequestEnabled)}
                className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${perRequestEnabled ? 'bg-blue-500' : 'bg-slate-300'}`}
              >
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${perRequestEnabled ? 'translate-x-7' : 'translate-x-1'}`} />
              </button>
            </div>
            {perRequestEnabled && (
              <div className="mt-3 ml-11">
                <div className="text-xs text-slate-500 mb-1.5">注入到 System Prompt 的结构：</div>
                <pre className="text-xs bg-white p-3 rounded-md border border-slate-200 overflow-x-auto text-slate-700">
{`<knowledge_context category="fewshot">
  <usage_guidance>参考风格创作</usage_guidance>
  <documents>
    <document relevance="0.95">语录内容...</document>
  </documents>
</knowledge_context>`}
                </pre>
              </div>
            )}
          </div>

          {/* Agentic tool toggle */}
          <div className={`mt-3 rounded-lg border p-4 ${agenticToolEnabled ? 'border-purple-200 bg-purple-50/40' : 'border-slate-200 bg-white'}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className={`p-2 rounded-lg ${agenticToolEnabled ? 'bg-purple-100' : 'bg-slate-100'}`}>
                  <Wrench className={`w-5 h-5 ${agenticToolEnabled ? 'text-purple-600' : 'text-slate-400'}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="font-medium text-slate-900">作为工具供 Agent 主动调用</h4>
                  <p className="text-sm text-slate-500 mt-0.5">
                    Agent 在需要时主动调用工具检索知识库。适合 Agent 判断是否需要查的场景，按需触发。
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => updateHookConfig('agentic_tool_enabled', !agenticToolEnabled)}
                className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${agenticToolEnabled ? 'bg-purple-500' : 'bg-slate-300'}`}
              >
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${agenticToolEnabled ? 'translate-x-7' : 'translate-x-1'}`} />
              </button>
            </div>
            {agenticToolEnabled && (
              <div className="mt-3 ml-11">
                <div className="text-xs text-slate-500 mb-1.5">工具名（建议在 System Prompt / instructions 中明确告知 Agent 此工具及触发条件）：</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-1.5 bg-white border border-slate-200 rounded-md text-sm font-mono text-purple-700">
                    search_knowledge_base
                  </code>
                  <button
                    type="button"
                    onClick={copyToolName}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-slate-200 rounded-md text-xs text-slate-600 hover:bg-slate-50 transition-colors"
                  >
                    {toolNameCopied ? <><Check className="w-3.5 h-3.5 text-green-600" />已复制</> : <><Copy className="w-3.5 h-3.5" />复制</>}
                  </button>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Retrieval Tuning (per_request only — Agno's built-in tool ignores these) */}
      {isEnabled && perRequestEnabled && (
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <Settings className="w-5 h-5 text-slate-600" />
            <h3 className="font-medium text-slate-900">每次注入的检索参数</h3>
            <span className="text-xs text-slate-500">（仅作用于 per_request 自动注入；分类规则可独立覆盖）</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">检索数量</label>
              <input
                type="number"
                min={1}
                max={20}
                value={knowledgeHookConfig.limit || 5}
                onChange={(e) => updateHookConfig('limit', parseInt(e.target.value) || 5)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
              />
              <p className="text-xs text-slate-500 mt-1">每次对话检索的文档数量</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">相关性阈值</label>
              <input
                type="number"
                min={0}
                max={1}
                step={0.1}
                value={knowledgeHookConfig.threshold ?? 0.3}
                onChange={(e) => updateHookConfig('threshold', parseFloat(e.target.value) || 0.0)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
              />
              <p className="text-xs text-slate-500 mt-1">最小相关性分数 (0-1)</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">内容截断</label>
              <input
                type="number"
                min={100}
                max={2000}
                step={100}
                value={knowledgeHookConfig.max_content_length || 500}
                onChange={(e) => updateHookConfig('max_content_length', parseInt(e.target.value) || 500)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
              />
              <p className="text-xs text-slate-500 mt-1">单文档最大字符数</p>
            </div>
          </div>

          {/* Usage Guidance */}
          <div className="mt-6 pt-6 border-t border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <label className="block text-sm font-medium text-slate-700">知识库使用说明</label>
                {knowledgeHookConfig.usage_guidance ? (
                  <span className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded">已自定义</span>
                ) : (
                  <span className="px-1.5 py-0.5 text-xs bg-slate-100 text-slate-500 rounded">默认</span>
                )}
              </div>
              {knowledgeHookConfig.usage_guidance && (
                <button
                  type="button"
                  onClick={() => updateHookConfig('usage_guidance', '')}
                  className="flex items-center gap-1 text-xs text-slate-500 hover:text-blue-600 transition-colors"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  重置为默认
                </button>
              )}
            </div>
            <textarea
              value={knowledgeHookConfig.usage_guidance || DEFAULT_USAGE_GUIDANCE}
              onChange={(e) => {
                const newValue = e.target.value === DEFAULT_USAGE_GUIDANCE ? '' : e.target.value;
                updateHookConfig('usage_guidance', newValue);
              }}
              className={`w-full h-32 px-3 py-2 border border-slate-300 rounded-md text-sm font-mono resize-y ${!knowledgeHookConfig.usage_guidance ? 'text-slate-500' : ''}`}
            />
            <p className="text-xs text-slate-500 mt-1">
              指导 Agent 如何使用检索到的知识库内容。将注入到系统提示词的 &lt;usage_guidance&gt; 标签中。
            </p>
          </div>

          {/* Per-Category Retrieval Rules */}
          <div className="mt-6 pt-6 border-t border-slate-200">
            <div
              className="flex items-center justify-between cursor-pointer select-none"
              onClick={() => setShowRulesSection(!showRulesSection)}
            >
              <div className="flex items-center gap-2">
                {showRulesSection ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                <Layers className="w-5 h-5 text-slate-600" />
                <h4 className="font-medium text-slate-900">分类检索规则</h4>
                {categoryRules.length > 0 && (
                  <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">{categoryRules.length}</span>
                )}
              </div>
              <p className="text-xs text-slate-500">为不同分类配置独立的检索策略</p>
            </div>

            {showRulesSection && (
              <div className="mt-4 space-y-3">
                {categoryRules.length === 0 && (
                  <div className="text-center py-6 bg-slate-50 rounded-lg border border-dashed border-slate-300">
                    <Layers className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500 mb-3">未配置分类规则时，将使用全局配置对全部文档进行检索</p>
                    <Button variant="outline" size="sm" onClick={addCategoryRule}>
                      <Plus className="w-4 h-4 mr-1" />
                      添加分类规则
                    </Button>
                  </div>
                )}

                {categoryRules.map((rule, idx) => {
                  const isEditing = editingRule === idx;
                  const color = rule.category ? getCategoryColor(rule.category) : null;

                  return (
                    <div
                      key={idx}
                      className={`border rounded-lg transition-all ${isEditing ? 'border-blue-300 bg-blue-50/30 shadow-sm' : 'border-slate-200 bg-white'}`}
                    >
                      {/* Rule header */}
                      <div
                        className="flex items-center justify-between px-4 py-3 cursor-pointer"
                        onClick={() => setEditingRule(isEditing ? null : idx)}
                      >
                        <div className="flex items-center gap-3">
                          <GripVertical className="w-4 h-4 text-slate-300" />
                          {color && <div className={`w-2.5 h-2.5 rounded-full ${color.dot}`} />}
                          <span className="font-medium text-sm text-slate-900">
                            {rule.category || <span className="text-slate-400 italic">未命名分类</span>}
                          </span>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${
                            rule.query_mode === 'fixed'
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-sky-100 text-sky-700'
                          }`}>
                            {rule.query_mode === 'fixed' ? <Zap className="w-3 h-3" /> : <MessageSquare className="w-3 h-3" />}
                            {rule.query_mode === 'fixed' ? '固定查询' : '跟随用户消息'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); removeCategoryRule(idx); }}
                            className="p-1 text-slate-400 hover:text-red-500 rounded transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                          {isEditing ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                        </div>
                      </div>

                      {/* Rule editor */}
                      {isEditing && (
                        <div className="px-4 pb-4 space-y-3 border-t border-slate-200/60">
                          <div className="grid grid-cols-2 gap-3 pt-3">
                            <div>
                              <label className="block text-xs font-medium text-slate-600 mb-1">分类名称 *</label>
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={rule.category}
                                  onChange={(e) => updateCategoryRule(idx, 'category', e.target.value.trim())}
                                  placeholder="fewshot"
                                  className="flex-1 px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                                />
                                {categories.length > 0 && (
                                  <select
                                    value={rule.category}
                                    onChange={(e) => updateCategoryRule(idx, 'category', e.target.value)}
                                    className="px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white"
                                  >
                                    <option value="">选择</option>
                                    {categories.map((cat) => (
                                      <option key={cat.name} value={cat.name}>{cat.name} ({cat.doc_count})</option>
                                    ))}
                                  </select>
                                )}
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-600 mb-1">查询模式</label>
                              <select
                                value={rule.query_mode}
                                onChange={(e) => updateCategoryRule(idx, 'query_mode', e.target.value)}
                                className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                              >
                                <option value="user_message">跟随用户消息</option>
                                <option value="fixed">固定查询词</option>
                              </select>
                            </div>
                          </div>

                          {rule.query_mode === 'fixed' && (
                            <div>
                              <label className="block text-xs font-medium text-slate-600 mb-1">固定查询词 *</label>
                              <input
                                type="text"
                                value={rule.fixed_query}
                                onChange={(e) => updateCategoryRule(idx, 'fixed_query', e.target.value)}
                                placeholder="经典语录 创意文案 表达方式"
                                className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                              />
                              <p className="text-xs text-slate-400 mt-1">每次对话使用此固定查询词进行检索，而非用户消息</p>
                            </div>
                          )}

                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-slate-600 mb-1">检索数量</label>
                              <input
                                type="number"
                                min={1}
                                max={20}
                                value={rule.limit ?? ''}
                                onChange={(e) => updateCategoryRule(idx, 'limit', e.target.value ? parseInt(e.target.value) : null)}
                                placeholder={`${knowledgeHookConfig.limit || 5}`}
                                className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                              />
                              <p className="text-xs text-slate-400 mt-0.5">空 = 使用全局值</p>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-600 mb-1">相关性阈值</label>
                              <input
                                type="number"
                                min={0}
                                max={1}
                                step={0.1}
                                value={rule.threshold ?? ''}
                                onChange={(e) => updateCategoryRule(idx, 'threshold', e.target.value ? parseFloat(e.target.value) : null)}
                                placeholder={`${knowledgeHookConfig.threshold || 0.3}`}
                                className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                              />
                              <p className="text-xs text-slate-400 mt-0.5">空 = 使用全局值</p>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-600 mb-1">内容截断</label>
                              <input
                                type="number"
                                min={100}
                                max={2000}
                                step={100}
                                value={rule.max_content_length ?? ''}
                                onChange={(e) => updateCategoryRule(idx, 'max_content_length', e.target.value ? parseInt(e.target.value) : null)}
                                placeholder={`${knowledgeHookConfig.max_content_length || 500}`}
                                className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                              />
                              <p className="text-xs text-slate-400 mt-0.5">空 = 使用全局值</p>
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">使用说明（该分类专属）</label>
                            <textarea
                              value={rule.usage_guidance}
                              onChange={(e) => updateCategoryRule(idx, 'usage_guidance', e.target.value)}
                              placeholder="空 = 使用全局使用说明"
                              rows={2}
                              className="w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm resize-y"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {categoryRules.length > 0 && (
                  <Button variant="outline" size="sm" onClick={addCategoryRule}>
                    <Plus className="w-4 h-4 mr-1" />
                    添加规则
                  </Button>
                )}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Document Management */}
      {isEnabled && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-slate-600" />
              <h3 className="font-medium text-slate-900">文档管理</h3>
              <span className="text-sm text-slate-500">({totalDocs || total} 个文档)</span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => { loadDocuments(); loadCategories(); }} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                刷新
              </Button>
              {total > 0 && (
                <Button variant="outline" size="sm" onClick={handleRecategorize} disabled={recategorizing || loading}>
                  {recategorizing ? '分类中...' : '重新分类'}
                </Button>
              )}
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setNewDocCategory(activeCategory || 'default');
                  setShowAddForm(true);
                }}
              >
                <Plus className="w-4 h-4 mr-1" />
                添加文档
              </Button>
              {total > 0 && (
                <Button variant="danger" size="sm" onClick={handleClearAll} disabled={loading}>
                  <Trash2 className="w-4 h-4 mr-1" />
                  清空
                </Button>
              )}
            </div>
          </div>

          {/* Error display */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600 flex items-center justify-between">
              {error}
              <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
            </div>
          )}

          {/* Category pills */}
          {categories.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={() => { setActiveCategory(''); setPage(0); setSearchResults(null); }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  activeCategory === ''
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <Tag className="w-3.5 h-3.5" />
                全部
                <span className={`text-xs ${activeCategory === '' ? 'text-slate-300' : 'text-slate-400'}`}>
                  {totalDocs}
                </span>
              </button>
              {categories.map((cat) => {
                const color = getCategoryColor(cat.name);
                const isActive = activeCategory === cat.name;
                const hasRule = categoryRules.some(r => r.category === cat.name);
                return (
                  <button
                    key={cat.name}
                    onClick={() => { setActiveCategory(isActive ? '' : cat.name); setPage(0); setSearchResults(null); }}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all border ${
                      isActive
                        ? `${color.bg} ${color.text} ${color.border} shadow-sm`
                        : `bg-white ${color.text} border-slate-200 hover:${color.bg}`
                    }`}
                  >
                    <div className={`w-2 h-2 rounded-full ${color.dot}`} />
                    {cat.name}
                    <span className="text-xs opacity-60">{cat.doc_count}</span>
                    {hasRule && <Zap className="w-3 h-3 opacity-50" />}
                  </button>
                );
              })}
            </div>
          )}

          {/* Search bar */}
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder={activeCategory ? `在 ${activeCategory} 中搜索...` : '搜索文档内容...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-md text-sm"
              />
            </div>
            <Button variant="outline" size="sm" onClick={handleSearch} disabled={loading}>搜索</Button>
            {searchResults && (
              <Button variant="ghost" size="sm" onClick={handleClearSearch}><X className="w-4 h-4" /></Button>
            )}
          </div>

          {searchResults && (
            <div className="mb-4 text-sm text-slate-600">找到 {searchResults.length} 个相关文档</div>
          )}

          {/* Add document form */}
          {showAddForm && (
            <div className="mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h4 className="font-medium text-slate-900 mb-3">添加新文档</h4>

              {/* Category field first */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  <Tag className="w-3.5 h-3.5 inline mr-1" />
                  分类 *
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newDocCategory}
                    onChange={(e) => setNewDocCategory(e.target.value)}
                    placeholder="default"
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm"
                  />
                  {categories.length > 0 && (
                    <select
                      value={categories.some(c => c.name === newDocCategory) ? newDocCategory : ''}
                      onChange={(e) => { if (e.target.value) setNewDocCategory(e.target.value); }}
                      className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
                    >
                      <option value="">选择已有分类</option>
                      {categories.map((cat) => (
                        <option key={cat.name} value={cat.name}>{cat.name} ({cat.doc_count})</option>
                      ))}
                    </select>
                  )}
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  {suggestingCategory
                    ? '正在智能推荐分类...'
                    : '输入新分类名或选择已有分类'}
                </p>
              </div>

              {/* Mode Tabs */}
              <div className="flex gap-2 mb-4">
                {([
                  { mode: 'text' as const, icon: FileText, label: '文本' },
                  { mode: 'file' as const, icon: Upload, label: '文件上传' },
                  { mode: 'url' as const, icon: Link, label: 'URL导入' },
                  { mode: 'web' as const, icon: Globe, label: '联网搜索' },
                ]).map(({ mode, icon: Icon, label }) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setAddMode(mode)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      addMode === mode ? 'bg-blue-500 text-white' : 'bg-white text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </button>
                ))}
              </div>

              <div className="space-y-3">
                {addMode === 'text' && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-slate-700">
                        文档内容 *
                      </label>
                      <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={bulkMode}
                          onChange={(e) => setBulkMode(e.target.checked)}
                          className="rounded"
                        />
                        批量模式（一行一条）
                      </label>
                    </div>
                    <textarea
                      value={newDocContent}
                      onChange={(e) => setNewDocContent(e.target.value)}
                      onBlur={() => {
                        if (!bulkMode && newDocContent.trim() && newDocCategory === 'default') {
                          handleSuggestCategory(newDocContent);
                        }
                      }}
                      placeholder={bulkMode ? '每行一条，例如：\n你不是脾气好 是在乎的人变少了\n该回的消息回不动 不想回的倒一直弹出来' : '输入文档内容...'}
                      rows={bulkMode ? 8 : 4}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                    />
                    {bulkMode && newDocContent.trim() && (
                      <p className="text-xs text-slate-500 mt-1">
                        共 {newDocContent.split('\n').filter(l => l.trim()).length} 条
                      </p>
                    )}
                  </div>
                )}

                {addMode === 'file' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">选择文件 * (PDF, DOCX, TXT, MD, JSON, CSV)</label>
                      <input
                        type="file"
                        accept=".pdf,.docx,.txt,.md,.json,.csv"
                        onChange={async (e) => {
                          const file = e.target.files?.[0] || null;
                          setSelectedFile(file);
                          if (file && newDocCategory === 'default') {
                            if (file.type?.includes('text') || file.name.match(/\.(txt|md|csv|json)$/i)) {
                              const reader = new FileReader();
                              reader.onload = () => {
                                const text = (reader.result as string).slice(0, 500);
                                handleSuggestCategory(file.name + '\n' + text);
                              };
                              reader.readAsText(file.slice(0, 1000));
                            } else {
                              handleSuggestCategory(file.name);
                            }
                          }
                        }}
                        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                      />
                      {selectedFile && (
                        <p className="mt-1 text-xs text-slate-500">已选择: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)</p>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">文档名称 (可选)</label>
                        <input type="text" value={newDocName} onChange={(e) => setNewDocName(e.target.value)} placeholder="默认使用文件名" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">描述 (可选)</label>
                        <input type="text" value={newDocDescription} onChange={(e) => setNewDocDescription(e.target.value)} placeholder="文档描述" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
                      </div>
                    </div>
                  </>
                )}

                {addMode === 'url' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">URL地址 *</label>
                      <input type="url" value={urlToImport} onChange={(e) => setUrlToImport(e.target.value)} placeholder="https://example.com/document" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">文档名称 (可选)</label>
                        <input type="text" value={newDocName} onChange={(e) => setNewDocName(e.target.value)} placeholder="文档名称" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">描述 (可选)</label>
                        <input type="text" value={newDocDescription} onChange={(e) => setNewDocDescription(e.target.value)} placeholder="文档描述" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
                      </div>
                    </div>
                  </>
                )}

                {addMode === 'web' && (
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={webSearchQuery}
                        onChange={(e) => setWebSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleWebSearch()}
                        placeholder="搜索关键词，如：经典语录 名言"
                        className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm"
                      />
                      <Button variant="primary" size="sm" onClick={handleWebSearch} disabled={webSearchLoading || !webSearchQuery.trim()}>
                        {webSearchLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                        搜索
                      </Button>
                    </div>

                    {webSearchResults.length > 0 && (
                      <>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {webSearchResults.map((r: any, i: number) => (
                            <label
                              key={i}
                              className={`flex gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                selectedResults.has(i) ? 'border-blue-300 bg-blue-50' : 'border-slate-200 bg-white hover:bg-slate-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedResults.has(i)}
                                onChange={() => toggleResultSelection(i)}
                                className="mt-1 rounded"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-900 line-clamp-1">{r.title}</p>
                                <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{r.summary || r.snippet}</p>
                                <p className="text-xs text-slate-400 mt-1 truncate">{r.url}</p>
                              </div>
                            </label>
                          ))}
                        </div>

                        {selectedResults.size > 0 && (
                          <div className="flex items-center gap-3 pt-2 border-t border-slate-200">
                            <span className="text-sm text-slate-600">已选 {selectedResults.size} 条</span>
                            <Button variant="outline" size="sm" onClick={handleDirectImport} disabled={loading}>
                              直接入库
                            </Button>
                            <Button variant="primary" size="sm" onClick={() => setShowCuratePanel(!showCuratePanel)}>
                              <Zap className="w-4 h-4 mr-1" />
                              AI 整理后入库
                            </Button>
                          </div>
                        )}

                        {showCuratePanel && selectedResults.size > 0 && (
                          <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 space-y-3">
                            <div className="flex items-center gap-2">
                              <Zap className="w-4 h-4 text-purple-600" />
                              <span className="text-sm font-medium text-purple-900">AI 整理</span>
                            </div>

                            <div className="flex gap-2 flex-wrap">
                              {CURATE_TEMPLATES.map((t) => (
                                <button
                                  key={t.label}
                                  type="button"
                                  onClick={() => setCuratePrompt(t.prompt)}
                                  className="px-2 py-1 text-xs bg-white border border-purple-200 rounded-full text-purple-700 hover:bg-purple-100"
                                >
                                  {t.label}
                                </button>
                              ))}
                              {promptHistory.length > 0 && (
                                <select
                                  value=""
                                  onChange={(e) => { if (e.target.value) setCuratePrompt(e.target.value); }}
                                  className="px-2 py-1 text-xs border border-purple-200 rounded-full bg-white text-purple-700"
                                >
                                  <option value="">历史提示词</option>
                                  {promptHistory.map((p, i) => (
                                    <option key={i} value={p}>{p.slice(0, 30)}...</option>
                                  ))}
                                </select>
                              )}
                            </div>

                            <textarea
                              value={curatePrompt}
                              onChange={(e) => setCuratePrompt(e.target.value)}
                              placeholder="输入整理提示词..."
                              rows={3}
                              className="w-full px-3 py-2 border border-purple-300 rounded-md text-sm"
                            />

                            <Button
                              variant="primary"
                              size="sm"
                              onClick={handleCurate}
                              disabled={curateLoading || !curatePrompt.trim()}
                            >
                              {curateLoading ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" />整理中...</> : '开始整理并入库'}
                            </Button>

                            {curatedPreview.length > 0 && (
                              <div className="space-y-2">
                                <p className="text-xs font-medium text-purple-700">已整理入库 {curatedPreview.length} 条:</p>
                                {curatedPreview.map((doc: any, i: number) => (
                                  <div key={i} className="p-2 bg-white rounded border border-purple-100 text-xs text-slate-700">
                                    <p className="line-clamp-3">{doc.content}</p>
                                    <p className="text-slate-400 mt-1">来源: {doc.source_url}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Metadata key-value form */}
                <details className="group">
                  <summary className="text-sm font-medium text-slate-500 cursor-pointer hover:text-slate-700">
                    附加属性 (可选) {metadataFields.length > 0 && <span className="text-xs text-blue-500 ml-1">{metadataFields.length} 项</span>}
                  </summary>
                  <div className="mt-2 space-y-2">
                    {metadataFields.map((field, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <input
                          type="text"
                          value={field.key}
                          onChange={(e) => {
                            const updated = [...metadataFields];
                            updated[i] = { ...updated[i], key: e.target.value };
                            setMetadataFields(updated);
                          }}
                          placeholder="属性名"
                          className="w-1/3 px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                        />
                        <input
                          type="text"
                          value={field.value}
                          onChange={(e) => {
                            const updated = [...metadataFields];
                            updated[i] = { ...updated[i], value: e.target.value };
                            setMetadataFields(updated);
                          }}
                          placeholder="属性值"
                          className="flex-1 px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => setMetadataFields(metadataFields.filter((_, j) => j !== i))}
                          className="p-1 text-slate-400 hover:text-red-500 rounded"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => setMetadataFields([...metadataFields, { key: '', value: '' }])}
                      className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" />
                      添加属性
                    </button>
                  </div>
                </details>

                {addMode !== 'web' ? (
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={resetAddForm}>取消</Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleAddDocument}
                      disabled={
                        loading ||
                        !newDocCategory.trim() ||
                        newDocCategory.length > 50 ||
                        (addMode === 'text' && !newDocContent.trim()) ||
                        (addMode === 'file' && !selectedFile) ||
                        (addMode === 'url' && !urlToImport.trim())
                      }
                    >
                      添加
                    </Button>
                  </div>
                ) : webSearchResults.length === 0 ? (
                  <div className="flex justify-end">
                    <Button variant="ghost" size="sm" onClick={resetAddForm}>取消</Button>
                  </div>
                ) : null}
              </div>
            </div>
          )}

          {/* Documents list */}
          <div className="max-h-[480px] overflow-y-auto">
            {loading && displayDocuments.length === 0 ? (
              <div className="text-center py-8 text-slate-500">加载中...</div>
            ) : displayDocuments.length === 0 ? (
              searchResults ? (
                <div className="text-center py-8 text-slate-500">没有找到相关文档</div>
              ) : (
                <div className="text-center py-12 border border-dashed border-slate-200 rounded-lg">
                  <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm font-medium text-slate-600 mb-1">
                    {activeCategory ? `"${activeCategory}" 分类下暂无文档` : '知识库还是空的'}
                  </p>
                  <p className="text-xs text-slate-400 mb-4">
                    添加文档后，Agent 对话时会自动检索相关内容作为上下文
                  </p>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      setNewDocCategory(activeCategory || 'default');
                      setShowAddForm(true);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    添加第一个文档
                  </Button>
                </div>
              )
            ) : (
              <KnowledgeDocList
                documents={displayDocuments}
                isSearchResult={!!searchResults}
                onDelete={handleDeleteDocument}
                activeCategory={activeCategory}
              />
            )}
          </div>

          {/* Pagination */}
          {!searchResults && total > pageSize && (
            <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-slate-100">
              <Button variant="ghost" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
                上一页
              </Button>
              <span className="text-sm text-slate-600">
                第 {page + 1} / {Math.ceil(total / pageSize)} 页
              </span>
              <Button variant="ghost" size="sm" onClick={() => setPage((p) => p + 1)} disabled={(page + 1) * pageSize >= total}>
                下一页
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Help */}
      <Card className="p-4 bg-slate-50 border-slate-200">
        <h4 className="text-sm font-medium text-slate-700 mb-2">知识库说明</h4>
        <ul className="text-xs text-slate-600 space-y-1">
          <li>• 知识库使用向量数据库（PgVector + HNSW 索引）存储和检索相关信息</li>
          <li>• 两种检索模式可独立启用或组合：
            <ul className="ml-4 mt-1 space-y-0.5">
              <li>– <strong>每次请求自动注入</strong>：hook 阶段统一检索并以 XML 块注入 system prompt（适合 fewshot、参考资料）</li>
              <li>– <strong>作为工具供 Agent 调用</strong>：注册 <code className="bg-white px-1 rounded">search_knowledge_base</code> 工具，Agent 自行决定是否调用（适合可选检索场景）</li>
            </ul>
          </li>
          <li>• 文档按<strong>分类（Category）</strong>组织，每个分类可独立配置检索策略</li>
          <li>• <strong>固定查询</strong>：使用固定查询词检索（适合 fewshot 语录、格式模板等）</li>
          <li>• <strong>跟随用户消息</strong>：根据用户当前问题语义检索（适合 FAQ、参考文档等）</li>
          <li>• 支持三种导入方式：文本输入、文件上传 (PDF/DOCX/TXT/MD/JSON/CSV)、URL导入</li>
          <li>• 多分类检索现已合并为单次 SQL（IN-filter），相较此前 N 次串行查询提升 8-9x 延迟</li>
        </ul>
      </Card>
    </div>
  );
};

export default KnowledgeTab;
